/**
 * Status vocabulary for the dashboard (task T.C2) — labels, tones and the
 * badge states the components draw.
 *
 * Pure on purpose: the `unit` Playwright project runs in plain Node, so the
 * badge states are tested as data and `StatusBadges.tsx` only renders them.
 * Tone semantics follow STYLEGUIDE §4 (green = done, red = lost, amber =
 * waiting on someone, brand = in flight, gray = nothing to say yet).
 */

import type { DashboardStatus, StatusCountMap } from '../../api/dashboard';

/**
 * All six buckets, in the order contract #11 serialises `byStatus` (the
 * `StatusCountMap` key order, which is `@cb-manager/shared`'s `ALL_STATUSES`).
 * A missing bucket is `0`, never absent — the UI iterates this list and only
 * this list, so a status added upstream shows up here first.
 *
 * Declared literally (not imported from the shared package) because this module
 * must stay importable from plain Node: the `unit` Playwright project has no
 * bundler, and the shared ESM build is extensionless.
 */
export const DASHBOARD_STATUSES: ReadonlyArray<DashboardStatus> = [
  'WAITING_LINK',
  'LINK_RECEIVED',
  'EVI_PASSED',
  'REJECTED',
  'AWAITING_INFO',
  'NO_DATA',
];

/** Tones accepted by the shared `<Badge>` (apps/web/src/components/ui.tsx). */
export type BadgeTone = 'neutral' | 'brand' | 'warn' | 'ok' | 'bad';

/**
 * THE RU status vocabulary of the whole app (T.R1) — one map, every page. The
 * upper-snake codes stay untouched on the wire (they are the api contract);
 * this is the only place they turn into words. Order matches the api's
 * `byStatus` serialisation, i.e. `DASHBOARD_STATUSES`, the only order the UI
 * ever iterates.
 */
export const STATUS_LABEL: Readonly<Record<DashboardStatus, string>> = {
  WAITING_LINK: 'Ждёт ссылку',
  LINK_RECEIVED: 'Получил ссылку',
  EVI_PASSED: 'Прошёл ЕВИ',
  REJECTED: 'Отказ',
  AWAITING_INFO: 'Ждёт доп. данных',
  NO_DATA: 'Нет данных',
};

/** Colour semantics per status (see the module doc). */
export const STATUS_TONE: Readonly<Record<DashboardStatus, BadgeTone>> = {
  WAITING_LINK: 'neutral',
  LINK_RECEIVED: 'brand',
  EVI_PASSED: 'ok',
  REJECTED: 'bad',
  AWAITING_INFO: 'warn',
  NO_DATA: 'neutral',
};

export function statusTone(status: DashboardStatus): BadgeTone {
  return STATUS_TONE[status] ?? 'neutral';
}

export function statusLabel(status: DashboardStatus): string {
  return STATUS_LABEL[status] ?? status;
}

/** A count that is not a sane count is `0` — the api serialises all six keys. */
export function normalizeCount(count: number | null | undefined): number {
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) return 0;
  return Math.round(count);
}

export interface BadgeState {
  status: DashboardStatus;
  /** `true` → render nothing (a compact badge never shows an empty bucket). */
  hidden: boolean;
  tone: BadgeTone;
  label: string;
  count: number;
  /** The count, printed as-is — the label lives in the card/tooltip. */
  text: string;
  title: string;
}

/**
 * The state of ONE badge. The invariant under test: a compact badge hides an
 * empty bucket, a summary badge always shows all six (a campaign card reading
 * `0` is information, not noise). The badge prints the bare count — the label
 * is the campaign card's caption or the tooltip, so a venue row stays one
 * compact line the way the six cards above the table already explained.
 */
export function badgeState(
  status: DashboardStatus,
  count: number | null | undefined,
  options: { compact?: boolean } = {},
): BadgeState {
  const value = normalizeCount(count);
  const compact = options.compact ?? false;
  const label = statusLabel(status);
  return {
    status,
    hidden: compact && value === 0,
    tone: statusTone(status),
    label,
    count: value,
    text: String(value),
    title: `${label}: ${value}`,
  };
}

/** One entry per non-zero bucket, in `DASHBOARD_STATUSES` order. */
export function nonzeroBadges(byStatus: Partial<StatusCountMap> | null | undefined): BadgeState[] {
  return DASHBOARD_STATUSES.filter((status) => normalizeCount(byStatus?.[status]) > 0).map((status) =>
    badgeState(status, byStatus?.[status], { compact: true }),
  );
}

/** All six buckets — the campaign summary cards. */
export function allBadges(byStatus: Partial<StatusCountMap> | null | undefined): BadgeState[] {
  return DASHBOARD_STATUSES.map((status) => badgeState(status, byStatus?.[status]));
}

/** Σ byStatus — the number of applications the buckets account for. */
export function sumByStatus(byStatus: Partial<StatusCountMap> | null | undefined): number {
  return DASHBOARD_STATUSES.reduce((total, status) => total + normalizeCount(byStatus?.[status]), 0);
}
